import {
  DO_NOT_USE_platformMetrics,
  type IAnalytics,
  type IReports,
} from '@sail/observability';
import toError from '@sail/observability/dist/helpers/toError/toError';
import {FormatError, ErrorCode as FormatErrorCode} from 'intl-messageformat';
import {ReactIntlError} from 'react-intl';
import {
  IntlInvalidConfigError,
  IntlMessageFormatError,
  IntlMessageFormatInvalidValueError,
  IntlMessageFormatMissingIntlAPIError,
  IntlMessageFormatMissingValueError,
  IntlMissingDataError,
  IntlMissingMessageError,
  IntlMissingTranslationError,
} from 'src/internal/errors';

import type {ErrorCode} from 'intl-messageformat';
import type {ReactIntlErrorCode} from 'react-intl';
import type {Locale, MessageDescriptor} from 'src/types';

const reportedErrors = new Set<string>();

// This function should never throw otherwise it will just break react-intl
// formatting fallbacks when there is an error
export const handleReactIntlError = (
  reports: IReports,
  analytics: IAnalytics,
  locale: Locale,
  maybeError: unknown,
): void => {
  const errorMessage = reactIntlErrorToSailIntlError(toError(maybeError));
  const message = errorMessage?.message;

  trackMissingTranslations(errorMessage);

  if (message && !reportedErrors.has(message)) {
    reportedErrors.add(message);

    if (errorMessage instanceof IntlMissingTranslationError) {
      const messageKey = errorMessage.descriptor?.id || 'NO_MESSAGE_KEY';
      analytics.track('i18n_translation_format_message_miss', {
        messageKey,
        locale,
      });
    } else if (errorMessage instanceof IntlMessageFormatError) {
      reports.warning(errorMessage);
    } else {
      reports.error(errorMessage);
    }
  }
};

// New class that extends FormatError to include a MessageDescriptor
class EnhancedFormatError extends FormatError {
  readonly descriptor?: MessageDescriptor;

  constructor(
    msg: string,
    code: ErrorCode,
    originalMessage?: string,
    descriptor?: MessageDescriptor,
  ) {
    super(msg, code, originalMessage);
    this.descriptor = descriptor;
  }
}

// New class that extends ReactIntlError to include a MessageDescriptor
class EnhancedReactIntlError<
  T extends ReactIntlErrorCode = ReactIntlErrorCode.FORMAT_ERROR,
> extends ReactIntlError<T> {
  readonly descriptor: MessageDescriptor;

  constructor(
    code: T,
    message: string,
    descriptor: MessageDescriptor,
    exception?: Error,
  ) {
    super(code, message, exception);
    this.descriptor = descriptor;
  }
}

function isReactIntlErrorCode(
  error: Error,
): error is EnhancedReactIntlError<ReactIntlErrorCode> | EnhancedFormatError {
  return 'code' in error;
}

function isReactIntlMessageDescriptor(
  error: Error,
): error is EnhancedReactIntlError<ReactIntlErrorCode> | EnhancedFormatError {
  return 'descriptor' in error;
}

export function reactIntlErrorToSailIntlError(
  error: Error | ReactIntlError,
): Error {
  if (isReactIntlErrorCode(error)) {
    switch (error.code) {
      case 'MISSING_TRANSLATION':
        return new IntlMissingTranslationError(
          error,
          isReactIntlMessageDescriptor(error) ? error.descriptor : undefined,
        );
      case 'FORMAT_ERROR':
        return new IntlMessageFormatError(error);
      case 'MISSING_DATA':
        return new IntlMissingDataError(error);
      case 'INVALID_CONFIG':
        return new IntlInvalidConfigError(error);
      case FormatErrorCode.MISSING_VALUE:
        return new IntlMessageFormatMissingValueError(error);
      case FormatErrorCode.INVALID_VALUE:
        return new IntlMessageFormatInvalidValueError(error);
      case FormatErrorCode.MISSING_INTL_API:
        return new IntlMessageFormatMissingIntlAPIError(error);
      default:
        break;
    }
  }

  if (
    error.message ===
    '[React Intl] An `id` must be provided to format a message.'
  ) {
    return new IntlMissingMessageError();
  }

  return error;
}

function trackMissingTranslations(errorMessage: Error) {
  const message = errorMessage?.message;
  if (message && errorMessage instanceof IntlMissingTranslationError) {
    DO_NOT_USE_platformMetrics.trackTransalationCount('miss');
    DO_NOT_USE_platformMetrics.translationCountForMessageLength(
      'miss',
      errorMessage.descriptor?.defaultMessage?.length ?? 0,
    );
  }
}
